// PasswordStrengthInput.tsx
import { Box, PasswordInput, Popover, Progress, Text } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

// ✅ PasswordRequirement Component (Reusable)
interface PasswordRequirementProps {
  meets: boolean;
  label: string;
}

function PasswordRequirement({ meets, label }: PasswordRequirementProps) {
  return (
    <Text
      color={meets ? "teal" : "red"}
      style={{ display: "flex", alignItems: "center" }}
      mt={7}
      size="sm"
    >
      {meets ? <IconCheck size={14} /> : <IconX size={14} />}
      <Box ml={10}>{label}</Box>
    </Text>
  );
}

// ✅ PasswordStrengthInput Component
interface PasswordStrengthInputProps {
  label?: string;
  placeholder?: string;
  onPasswordChange?: (data: {
    value: string;
    requirementsMet: boolean;
  }) => void;
}

const requirements = [
  { re: /[0-9]/, labelKey: "auth.passwordValidation.includesNumber" },
  { re: /[a-z]/, labelKey: "auth.passwordValidation.includesLowercase" },
  { re: /[A-Z]/, labelKey: "auth.passwordValidation.includesUppercase" },
  { re: /[$&+,:;=?@#|'<>.^*()%!-]/, labelKey: "auth.passwordValidation.includesSpecialSymbol" },
];

function getStrength(password: string) {
  let multiplier = password.length > 5 ? 0 : 1;

  requirements.forEach((requirement) => {
    if (!requirement.re.test(password)) {
      multiplier += 1;
    }
  });

  return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10);
}

export const PasswordStrengthInput: React.FC<PasswordStrengthInputProps> = ({
  label,
  placeholder,
  onPasswordChange,
}) => {
  const { t } = useTranslation();
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [value, setValue] = useState("");

  const resolvedLabel = label ?? t("auth.login.password");
  const resolvedPlaceholder = placeholder ?? t("auth.login.passwordPlaceholder");

  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={t(requirement.labelKey)}
      meets={requirement.re.test(value)}
    />
  ));

  const strength = getStrength(value);
  const color = strength === 100 ? "teal" : strength > 50 ? "yellow" : "red";

  // ✅ Trigger callback whenever password changes
  // ✅ Trigger callback with two values: requirementsMet and value
  const handlePasswordChange = (newPassword: string) => {
    setValue(newPassword);

    const requirementsMet = requirements.every((req) =>
      req.re.test(newPassword)
    );

    if (onPasswordChange) {
      onPasswordChange({
        value: newPassword,
        requirementsMet,
      });
    }
  };

  return (
    <Popover
      opened={popoverOpened}
      position="bottom"
      width="target"
      transitionProps={{ transition: "pop" }}
    >
      <Popover.Target>
        <div
          onFocusCapture={() => setPopoverOpened(true)}
          onBlurCapture={() => setPopoverOpened(false)}
        >
          <PasswordInput
            withAsterisk
            label={resolvedLabel}
            placeholder={resolvedPlaceholder}
            value={value}
            onChange={(event) =>
              handlePasswordChange(event.currentTarget.value)
            }
          />
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <Progress color={color} value={strength} size={5} mb="xs" />
        <PasswordRequirement
          label={t("auth.passwordValidation.includesMinChars")}
          meets={value.length > 5}
        />
        {checks}
      </Popover.Dropdown>
    </Popover>
  );
};

export default PasswordStrengthInput;
